import { HttpErrorResponse } from '@angular/common/http';
import { booleanAttribute, Component, computed, contentChild, DestroyRef, effect, forwardRef, HostBinding, inject, input, model, output, signal, untracked, viewChild, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors, Validator } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { catchError, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { HisControlValueAccessor } from '../../../../core/forms/his-control-value-accessor';
import { GenericValue } from '../../../../core/model/generic-value';
import { splitTrimmed, trimToNull } from '../../../../core/utils/his-string.utils';
import { getBaseUrl } from '../../../../core/utils/his-url.utils';
import { HisBaseGlobalsService } from '../../../../core/service/base/his-base-globals.service';
import { HisRestoreFocusOrchestratorService } from '../../../../core/service/base/his-restore-focus-orchestrator.service';
import { HisUiService } from '../../../../core/service/base/his-ui.service';
import { HelptextProvider, LabelProvider, RequiredProvider } from '../../../../structure/component/plam/his-plam.component';
import { HisUploadConfiguration } from '../../../model/his-upload-configuration';
import { HisUploadFile } from '../../../model/his-upload-file';
import { HisFileUploadService } from '../../../service/his-file-upload.service';
import { HisFileUploadComponent } from '../../his-file-upload.component';
import { HisSelectFile, hisSelectFileKey, hisSelectFileName } from '../model/his-select-file.model';
import { HisSelectFileChangeEvent, HisSelectFileChangeReason } from '../model/his-select-file-change-event.model';
import { EudiwMedia } from '../model/eudiw-media.model';
import { EudiwMediaQuery } from '../model/eudiw-media-query.model';
import { documentMetadataObjGuidOf, LocalMedia } from '../model/local-media.model';
import { isUploadSourceType, UploadSourceType } from '../model/upload-source-type.model';
import { HisSelectFilePropertyDisplayDirective, HisSelectFilePropertyDisplayIconDirective } from '../directive/his-select-file-property-template.directive';
import { HisSelectFileService } from '../service/his-select-file.service';
import { HisSelectFileCategoryCoordinator } from './his-select-file-category.coordinator';
import { HisSelectFileItemComponent } from './item/his-select-file-item.component';
import { triggerControlValidation } from '../../../../core/utils/his-form.utils';

/**
 * Normalizes the {@code propertyCustomDisplayCategories} input value (a
 * comma-separated string or an array of strings) to a trimmed, non-empty
 * {@code string[]}.
 */
function normalizeCategoryKeys( value: string | string[] ): string[] {
    const parts = Array.isArray( value ) ? value.map( part => part.trim() ) : splitTrimmed( value ?? '', ',' );
    return parts.filter( Boolean );
}

/**
 * Normalizes the {@code sourceTypes} input value (a comma- or blank-separated string or an
 * array of strings) to the known {@link UploadSourceType} keys, mirroring the JSF
 * {@code sourceTypesFromAttribute()}: unknown values are silently dropped.
 */
function normalizeSourceTypes( value: string | string[] ): UploadSourceType[] {
    const parts = Array.isArray( value ) ? value.map( part => part.trim() ) : splitTrimmed( value ?? '', /[\s,]+/ );
    return parts.filter( isUploadSourceType );
}

function normalizeLocalMediaResourceInfo( value: string | Iterable<string> | null | undefined ): string[] {
    const parts = value == null ? ['description', 'category']
        : typeof value === 'string' ? splitTrimmed( value, /[\s,]+/ ) : Array.from( value );
    return parts.filter( part => part === 'description' || part === 'category' );
}

/**
 * The selection carried by a value the surrounding form writes into this component, or
 * {@code undefined} when it carries none.
 *
 * Anything that is not an array counts as "no selection": a {@code <his-select-file ngModel />}
 * without a value binds the empty string of the attribute, which {@link NgModel} passes on as
 * the initial model — every derivation of the selection would trip over it.
 */
function selectionOf( value: unknown ): HisSelectFile[] | undefined {
    return Array.isArray( value ) ? value : undefined;
}

/** Adds the persistent upload UUIDs without modifying the existing set. */
function withPersistentFileObjGuids( guids: ReadonlySet<string>, files: HisSelectFile[] | undefined ): ReadonlySet<string> {
    const next = new Set( guids );
    for ( const file of files ?? [] ) {
        const upload = file.upload;
        if ( upload?.id != null && upload.uuid != null ) {
            next.add( upload.uuid );
        }
    }

    return next.size === guids.size ? guids : next;
}

/**
 * Keys the PrimeNG accordion uses to move the focus between its headers.
 */
/**
 * The keys {@code Accordion} handles for its header navigation.
 *
 * Named after {@code KeyboardEvent.code}, the physical key, because that is what
 * {@code AccordionHeader.onKeydown()} switches on. For these keys the physical key and the
 * character it stands for are the same on every layout.
 */
const ACCORDION_NAVIGATION_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

/** Style class applied to the host element while none was requested through the input. */
const DEFAULT_STYLE_CLASS = '';

/** i18n keys of the button that opens the selection overlay, by state of the selection. */
const TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_EMPTY = 'common.components.selectFile.selectFile';
const TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_NOT_EMPTY_SINGLE = 'common.components.selectFile.changeSelectedFile';
const TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_NOT_EMPTY_MULTIPLE = 'common.components.selectFile.addSelectedFile';

/** i18n keys of the title of the selection overlay, by state of the selection. */
const TRANSLATION_OVERLAY_TITLE_CURRENTLY_EMPTY = 'common.components.selectFile.overlay.title.selectFile';
const TRANSLATION_OVERLAY_TITLE_CURRENTLY_NOT_EMPTY_SINGLE = 'common.components.selectFile.overlay.title.changeSelectedFile';
const TRANSLATION_OVERLAY_TITLE_CURRENTLY_NOT_EMPTY_MULTIPLE = 'common.components.selectFile.overlay.title.addSelectedFile';

/**
 * Relative URL of the JSF flow presenting the configuration of an upload field; the
 * {@code configKey} is appended as a request parameter.
 */
const UPLOAD_CONFIGURATION_FLOW = '/pages/startFlow.xhtml?_flowId=uploadConfiguration-flow&configKey=';

/**
 * Accordion value of the upload panel in the selection overlay. It is the preferred
 * panel, i.e. the one expanded whenever the overlay opens — mirroring the JSF
 * component, which defaults to the upload tab.
 */
const UPLOAD_SELECTION_PANEL = '1';

/**
 * Accordion value of the local-media panel in the selection overlay. It is expanded
 * whenever the overlay opens without an available upload panel.
 */
const LOCAL_MEDIA_SELECTION_PANEL = '0';

/** Accordion value of the EUDI-Wallet panel in the selection overlay. */
const EUDIW_SELECTION_PANEL = '2';

type EudiwQueryState =
    | { status: 'idle' }
    | { status: 'loading'; query?: EudiwMediaQuery }
    | { status: 'ready'; query: EudiwMediaQuery }
    | { status: 'error'; query?: EudiwMediaQuery };

type EudiwApplyState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; reason: 'unavailable' | 'queryNotFound' | 'technical' };

/**
 * Lets the user select one or more files — either by uploading them or by choosing from
 * the files that are already stored for the current person ("local media").
 *
 * Renders nothing as long as the upload field behind the {@link configKey} has no
 * configuration, mirroring {@code <his-file-upload />}: a field that is hidden or unknown to
 * the server is not presented to the user at all.
 *
 * This is the Angular counterpart of the JSF {@code <his:selectFile />} composite
 * component.
 */
@Component( {
    standalone: false,
    selector: 'his-select-file',
    templateUrl: './his-select-file.component.html',
    styleUrl: './his-select-file.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef( () => HisSelectFileComponent ),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef( () => HisSelectFileComponent ),
            multi: true,
        },
    ],
} )
export class HisSelectFileComponent extends HisControlValueAccessor implements Validator, LabelProvider, HelptextProvider, RequiredProvider {

    // Inputs
    /**
     * The selected file(s). Supports the two-way binding {@code [(value)]} as well as the
     * {@link ControlValueAccessor} of a surrounding form; both write the same model.
     *
     * Every change — no matter whether it was made by the user, by the form or through the
     * two-way binding — is reported by {@link selectionChange}.
     */
    readonly value = model<HisSelectFile[]>();
    readonly configKey = input.required<string>();

    /**
        * Optional i18n key or already translated text used instead of the label from the upload configuration.
     */
    readonly labelOverride = input<string>( undefined, { alias: 'label' } );

    /**
     * Custom CSS class to apply to the host element.
     * If absent, no additional style class is added beyond the defaults.
     * Mirrors the JSF {@code styleClass} attribute of {@code <his:selectFile />}.
     */
    readonly styleClass = input<string>();

    /**
     * Show the component in a disabled state.
     * Mirrors the JSF {@code disabled} attribute of {@code <his:selectFile />}.
     *
     * Combined with the state set by the surrounding form through {@link setDisabledState},
     * see {@link isDisabled}.
     */
    readonly disabled = input( false, { transform: booleanAttribute } );

    /**
     * Show the component in a readonly state.
     * Combined with the {@code readonly} flag from the upload configuration,
     * mirroring JSF {@code isReadonlyIntern()}.
     * Mirrors the JSF {@code readonly} attribute of {@code <his:selectFile />}.
     */
    readonly readonly = input( false, { transform: booleanAttribute } );

    /**
     * Overrides the configuration setting {@link HisUploadConfiguration.required}.
     * If the value is {@code undefined}, the setting from the configuration applies.
     */
    readonly requiredOverride = input<boolean | undefined, unknown>(
        undefined,
        { alias: 'required', transform: booleanAttribute }
    );

    /**
     * Overrides the upload sources configured for the upload field
     * ({@link HisUploadConfiguration.sourceTypes}). Accepts either a comma- or
     * blank-separated string or an array of {@link UploadSourceType} keys; unknown values
     * are dropped. While empty, the configured sources apply.
     */
    readonly sourceTypes = input<UploadSourceType[], string | string[]>( [], { transform: normalizeSourceTypes } );

    readonly optionalLocalMediaResourceInfo = input<string[], string | Iterable<string> | null | undefined>(
        ['description', 'category'],
        { transform: normalizeLocalMediaResourceInfo }
    );

    /**
     * Whether process properties should be shown/handled by this component.
     */
    readonly processProperties = input( false, { transform: booleanAttribute } );

    /**
     * Property category keys ({@link ResourcePropertyDetail.categoryKey}) whose
     * properties should be treated as "custom" rather than shown in the default
     * resource-properties overlay panel.
     *
     * Accepts either a comma-separated string or an array of single strings; both
     * are normalized to a trimmed, non-empty {@code string[]}.
     */
    readonly propertyCustomDisplayCategories = input<string[], string | string[]>(
        [],
        { transform: normalizeCategoryKeys }
    );

    /**
     * Optional template projected as a named child (via the
     * {@code hisPropertyCustomDisplayIcon} directive). It is not rendered by this
     * component directly; instead it is forwarded to each
     * {@link HisSelectFileItemComponent}, which renders it between the download
     * link and the action buttons. The current {@link HisSelectFile} is provided
     * as the template's implicit context, and the item's custom properties map
     * under the {@code properties} context key.
     */
    private readonly propertyCustomDisplayIconDirective = contentChild( HisSelectFilePropertyDisplayIconDirective );

    /** Typed template reference of the {@code hisPropertyCustomDisplayIcon} template, if provided. */
    readonly propertyCustomDisplayIcon = computed( () => this.propertyCustomDisplayIconDirective()?.templateRef );

    /**
     * Optional template projected as a named child (via the
     * {@code hisPropertyCustomDisplay} directive). It is not rendered by this
     * component directly; instead it is forwarded to each
     * {@link HisSelectFileItemComponent}, which renders it inside the
     * resource-properties overlay panel. The current {@link HisSelectFile} is
     * provided as the template's implicit context, and the item's custom
     * properties map under the {@code properties} context key.
     */
    private readonly propertyCustomDisplayDirective = contentChild( HisSelectFilePropertyDisplayDirective );

    /** Typed template reference of the {@code hisPropertyCustomDisplay} template, if provided. */
    readonly propertyCustomDisplay = computed( () => this.propertyCustomDisplayDirective()?.templateRef );

    /**
     * Emitted whenever the selected file(s) change, so that the surrounding template
     * can react to the selection — e.g. by showing a hint while nothing has been
     * selected, or by listing how many (and which) files are currently selected.
     *
     * The event fires on every value change: on the actions of the user, on writes coming
     * from the surrounding form (loading or resetting its data) and on writes made through
     * the two-way binding {@code [(value)]}. Use {@link HisSelectFileChangeEvent.reason} to
     * tell the triggers apart.
     */
    readonly selectionChange = output<HisSelectFileChangeEvent>();

    /**
     * The selection carried by the most recently emitted {@link selectionChange}. Lets the
     * component tell a change it has reported itself from one written from the outside.
     */
    private emittedSelection: HisSelectFile[] | undefined;

    /** Controls the visibility of the "change selected file" dialog. */
    protected readonly showChangeDialog = signal( false );

    /**
     * Draft model bound to the embedded {@link HisFileUploadComponent} inside the
     * upload panel. It holds only the file(s) the user is currently uploading and
     * is intentionally kept separate from {@link value} — existing selections are
     * never fed back into the upload component. Reset whenever the overlay opens,
     * the selection is applied, or the overlay is cancelled.
     *
     * A plain signal, not a {@code model()}: it belongs to the overlay of this component
     * and is never bound from the outside.
     */
    protected readonly uploadDraft = signal<HisUploadFile[]>( [] );

    /** Reference to the embedded upload component, used to reset its internal state. */
    private readonly fileUpload = viewChild( HisFileUploadComponent );

    /** The rendered selection, one item per selected file, used to move the focus to a file. */
    private readonly fileItems = viewChildren( HisSelectFileItemComponent );

    /** The selection overlay, whose focus restore has to be redirected to the applied file. */
    private readonly selectionDialog = viewChild( Dialog );

    /**
     * Reference to the embedded upload component's {@link NgModel}, used to surface its validation.
     *
     * Deliberately queried by the template reference {@code #uploadModel} and not by the
     * {@link NgModel} type: the category autocomplete carries an {@link NgModel} as well and is
     * declared before the overlay, so a type query would return that one instead.
     */
    private readonly uploadModel = viewChild<NgModel>( 'uploadModel' );

    /**
     * Value of the currently expanded accordion panel in the selection overlay.
     * Reset whenever the overlay opens: the only available source is expanded, while
     * {@code null} keeps every panel collapsed as long as several sources are available.
     */
    protected readonly activeSelectionPanel = signal<string | number | string[] | number[] | null>( null );

    /** `true` while the local-media panel of the selection overlay is expanded, i.e. shows its content. */
    private readonly localMediaPanelExpanded = computed<boolean>( () => {
        const active = this.activeSelectionPanel();
        const expanded = Array.isArray( active ) ? active : [active];
        return expanded.some( ( panel ) => String( panel ) === LOCAL_MEDIA_SELECTION_PANEL );
    } );

    /** `true` while the EUDI-Wallet panel is expanded. */
    private readonly eudiwPanelExpanded = computed<boolean>( () => {
        const active = this.activeSelectionPanel();
        const expanded = Array.isArray( active ) ? active : [active];
        return expanded.some( ( panel ) => String( panel ) === EUDIW_SELECTION_PANEL );
    } );

    /**
     * What the selection action does in the current state: pick the first file, replace the
     * single one or add another. The button and the title of the overlay only differ in the
     * i18n keys they pick for it.
     */
    private readonly editSelectionMode = computed<'select' | 'change' | 'add'>( () => {
        if ( this.empty() ) {
            return 'select';
        }

        return this.configuration()?.multiple ? 'add' : 'change';
    } );

    /** i18n key for the button that opens the file-selection overlay. */
    protected readonly editSelectionLabelKey = computed<string>( () => ( {
        select: TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_EMPTY,
        change: TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_NOT_EMPTY_SINGLE,
        add: TRANSLATION_CHANGE_SELECTION_LABEL_CURRENTLY_NOT_EMPTY_MULTIPLE,
    }[this.editSelectionMode()] ) );

    /** i18n key for the title of the file-selection overlay. */
    protected readonly editSelectionTitleKey = computed<string>( () => ( {
        select: TRANSLATION_OVERLAY_TITLE_CURRENTLY_EMPTY,
        change: TRANSLATION_OVERLAY_TITLE_CURRENTLY_NOT_EMPTY_SINGLE,
        add: TRANSLATION_OVERLAY_TITLE_CURRENTLY_NOT_EMPTY_MULTIPLE,
    }[this.editSelectionMode()] ) );

    /** Disabled state requested by the surrounding form through {@link setDisabledState}. */
    private readonly disabledByForm = signal( false );

    /**
     * `true` when the component is effectively disabled, either through the {@link disabled}
     * input or because the surrounding form disabled the control (e.g. `[disabled]` on the
     * `formControl`, or `FormControl.disable()`).
     */
    readonly isDisabled = computed<boolean>( () => this.disabled() || this.disabledByForm() );

    /** `true` when the component is effectively readonly (input flag or configuration). */
    protected readonly isReadonly = computed<boolean>( () =>
        this.readonly() || ( this.configuration()?.readonly ?? false )
    );

    /**
     * `true` when the upload configuration allows an individual comment per file,
     * mirroring the JSF {@code MergedUploadFieldDto.isAllowComment()}.
     */
    protected readonly allowComment = computed<boolean>( () =>
        this.configuration()?.showDescription ?? false
    );

    /** `true` when no file has been selected yet, `false` otherwise. */
    readonly empty = computed<boolean>( () => ( this.value()?.length ?? 0 ) === 0 );

    /**
     * `true` when the user may still add more files — i.e. the current file
     * count has not yet reached the configured maximum.
     *
     * Rules (mirroring the JSF component):
     * - readonly → never allow (the selection is displayed as plain text)
     * - no configuration → never allow; unreachable through the template, which does not
     *   render anything without a configuration
     * - {@code multiple} is false → always allow (user can replace the single file)
     * - {@code multiple} is true and {@code fileLimit > 0} → allow while count < fileLimit
     * - {@code multiple} is true and {@code fileLimit <= 0} → always allow (unlimited)
     */
    protected readonly canEditSelection = computed<boolean>( () => {
        const config = this.configuration();
        const currentCount = this.value()?.length ?? 0;

        if ( this.isReadonly() ) {
            return false;
        }

        if ( !config ) {
            return false;
        }

        if ( config.multiple && config.fileLimit > 0 ) {
            return currentCount < config.fileLimit;
        }

        return true;
    } );

    /**
     * CSS classes for the host element, mirroring {@code CCUIInput.getCss()}:
     * <ol>
     *   <li>{@code styleClass} input — or no additional style class when absent</li>
     *   <li>{@code input_disabled} when the component is disabled</li>
     *   <li>{@code input_readonly} when the component or its configuration is readonly</li>
     *   <li>The {@code css} value from the upload configuration (custom style classes)</li>
     * </ol>
     */
    private readonly css = computed<string>( () => {
        const classes: string[] = ['his-select-file', this.styleClass() ?? DEFAULT_STYLE_CLASS];

        if ( this.isDisabled() ) {
            classes.push( 'input_disabled' );
        }

        if ( this.isReadonly() ) {
            classes.push( 'input_readonly' );
        }

        const configCss = this.configuration()?.css;
        if ( configCss ) {
            classes.push( configCss );
        }

        return classes.join( ' ' );
    } );

    /**
     * Puts {@link css} on the host element. Written as a binding rather than as a wrapper
     * element in the template, so that consumers style {@code <his-select-file />} itself
     * instead of an element only this component knows about.
     */
    @HostBinding( 'class' )
    get hostClass(): string {
        return this.css();
    }

    /**
     * URL of the JSF flow showing the upload configuration behind the current
     * {@link configKey}, mirroring the JSF {@code showUploadConfiguration} link.
     */
    protected readonly configurationUrl = computed<string>( () =>
        getBaseUrl() + UPLOAD_CONFIGURATION_FLOW + encodeURIComponent( this.configKey() )
    );

    /**
     * `true` while additional information for administrators has to be presented — here the
     * link to the upload configuration —, mirroring the JSF
     * {@code configUtil.isShowExtendedInformation()}.
     *
     * Intentionally a getter rather than a signal: it shields the template from the
     * {@link HisBaseGlobalsService} and {@link HisBaseGlobalsService.dataDictionaryEditMode()}
     * is a plain property that is toggled outside of this component.
     *
     * TODO: Weil die Property kein Signal ist, bemerkt die Komponente einen Wechsel nur,
     * solange die zonenbasierte Change Detection den Getter erneut auswertet. Sobald der
     * HisBaseGlobalsService ein Signal anbietet, ist hier auf ein computed umzustellen —
     * ohne das bricht die Anzeige bei einer Umstellung auf OnPush oder zoneless.
     */
    protected get showConfigurationLink(): boolean {
        return this.baseGlobals.dataDictionaryEditMode();
    }

    private onValidatorChange?: () => void;

    /**
     * `true` when an input is mandatory. The {@code required} attribute of the
     * component takes precedence over {@link HisUploadConfiguration.required}.
     */
    protected readonly isRequired = computed<boolean>( () =>
        this.requiredOverride() ?? this.configuration()?.required ?? false
    );

    // LabelProvider / HelptextProvider / RequiredProvider
    // Getters over the configuration signal rather than properties written when the
    // configuration arrives: <his-plam /> reads them while checking its own template, so a
    // getter always answers with the current configuration instead of the one of the
    // previous change detection turn.

    /** Read by the surrounding {@code <his-plam />} via {@link LabelProvider}. */
    get ddLabeltext(): string {
        return this.labelOverride() ?? this.configuration()?.label;
    }

    /**
     * The label of the upload field, already translated, for use as a parameter of texts that
     * embed it — e.g. the label of the button opening the selection overlay.
     *
    * Necessary because {@link ddLabeltext} has to keep answering with the raw label value:
    * the surrounding {@code <his-plam />} resolves i18n keys itself.
     */
    protected readonly translatedLabel = computed<string>( () =>
        this.hisUiService.translate( this.ddLabeltext ?? '' ) ?? ''
    );

    /** Read by the surrounding {@code <his-plam />} via {@link HelptextProvider}. */
    get ddHelptext(): string {
        return this.configuration()?.helptextIcon;
    }

    /** Read by the surrounding {@code <his-plam />} via {@link RequiredProvider}. */
    get required(): boolean {
        return this.isRequired();
    }

    private readonly uploadService = inject( HisFileUploadService );
    private readonly baseGlobals = inject( HisBaseGlobalsService );
    private readonly hisUiService = inject( HisUiService );
    private readonly selectFileService = inject( HisSelectFileService );
    private readonly restoreFocus = inject( HisRestoreFocusOrchestratorService );
    private readonly destroyRef = inject( DestroyRef );

    /**
     * The configuration of the upload field behind the current {@link configKey}, reloaded
     * whenever the key changes.
     *
     * {@code null} while the first response is still pending, while the upload field has no
     * configuration at all — a hidden or unknown field is answered with an empty body, which
     * {@link HisFileUploadService} swallows — and after a failed request. The template renders
     * nothing in that state, mirroring {@code <his-file-upload />}.
     */
    protected readonly configuration = toSignal(
        toObservable( this.configKey ).pipe(
            switchMap( ( configKey ) => this.uploadService.getUploadFieldConfig( configKey ).pipe(
                map( ( response ) => new HisUploadConfiguration( response ) ),
                catchError( ( error ) => {
                    console.error( 'his-select-file: could not load the upload configuration for', configKey, error );
                    return of( null );
                } )
            ) )
        ),
        { initialValue: null as HisUploadConfiguration | null }
    );

    /**
     * The document-metadata category shared by all selected files, in an own collaborator:
     * determining, presenting, applying and validating it is a concern of its own.
     */
    protected readonly categories = new HisSelectFileCategoryCoordinator( {
        files: computed( () => this.value() ?? [] ),
        configuration: this.configuration,
        locked: computed( () => this.isDisabled() || this.isReadonly() ),
        apply: ( files ) => this.applyCategorizedFiles( files ),
    } );

    /**
     * The files that are already available for the current person and may be selected
     * instead of uploading a new one, mirroring the JSF {@code getAllLocalMediaResources()}.
     *
     * Loaded on demand by {@link loadLocalMedia}, so it is empty until then.
     */
    protected readonly localMedia = signal<LocalMedia[]>( [] );

    /** Kept for this component's lifetime, including after removal, form reset or a record change. */
    private readonly excludedPersistentFileObjGuids = signal<ReadonlySet<string>>( new Set<string>() );

    /** `true` once a request for the local media of the current {@link configKey} has answered. */
    protected readonly localMediaLoaded = signal( false );

    /** The running or answered request behind {@link localMedia}, together with its config key. */
    private localMediaRequest?: Observable<LocalMedia[]>;
    private localMediaRequestKey?: string;

    /**
     * The upload sources the user may choose from, mirroring the JSF
     * {@code availableSourceTypes()}: the {@link sourceTypes} input wins, then the source
     * types of the upload configuration — and while both are empty,
     * {@link UploadSourceType.UPLOAD} applies as the default.
     */
    private readonly availableSourceTypes = computed<UploadSourceType[]>( () => {
        const overriddenSourceTypes = this.sourceTypes();
        if ( overriddenSourceTypes.length > 0 ) {
            return overriddenSourceTypes;
        }

        // The configuration carries the raw values of the server, which are narrowed to the
        // sources this component actually knows — just like the values of the input.
        const configuredSourceTypes = ( this.configuration()?.sourceTypes ?? [] ).filter( isUploadSourceType );
        return configuredSourceTypes.length === 0 ? [UploadSourceType.UPLOAD] : configuredSourceTypes;
    } );

    /**
     * `true` when the local-media panel has to be offered: {@link UploadSourceType.LOCAL_MEDIA}
     * has to be an available source.
     *
     * Deliberately narrower than the JSF {@code isLocalMediaAvailable()}, which also demands at
     * least one selectable file: the files are only loaded once the panel is opened, so whether
     * there are any cannot be part of the decision to offer it. An empty list is answered by the
     * panel itself.
     */
    protected readonly localMediaAvailable = computed<boolean>( () =>
        this.availableSourceTypes().includes( UploadSourceType.LOCAL_MEDIA )
    );

    /**
     * `true` when the upload panel has to be offered, mirroring the JSF
     * {@code isUploadAvailable()}: {@link UploadSourceType.UPLOAD} has to be an available
     * source.
     */
    protected readonly uploadAvailable = computed<boolean>( () =>
        this.availableSourceTypes().includes( UploadSourceType.UPLOAD )
    );

    /** `true` when the upload configuration offers an EUDI-Wallet selection. */
    protected readonly eudiwAvailable = computed<boolean>( () =>
        this.availableSourceTypes().includes( UploadSourceType.EUDIW )
    );

    /** The panels that can currently be offered in the selection overlay. */
    private readonly availableSelectionPanels = computed<string[]>( () => [
        ...( this.localMediaAvailable() ? [LOCAL_MEDIA_SELECTION_PANEL] : [] ),
        ...( this.eudiwAvailable() ? [EUDIW_SELECTION_PANEL] : [] ),
        ...( this.uploadAvailable() ? [UPLOAD_SELECTION_PANEL] : [] ),
    ] );

    /** `true` when the choice between several sources needs an explanatory hint. */
    protected readonly showEditSelectionHelptext = computed<boolean>( () =>
        this.availableSelectionPanels().length > 1
    );

    /** State of the EUDI query shown as QR code and redirect URL. */
    protected readonly eudiwQueryState = signal<EudiwQueryState>( { status: 'idle' } );

    /** State of the attempt to retrieve and apply the resource supplied by the wallet. */
    protected readonly eudiwApplyState = signal<EudiwApplyState>( { status: 'idle' } );

    /** The ready EUDI query, or `null` while no query can be displayed. */
    protected readonly eudiwQuery = computed<EudiwMediaQuery | null>( () => {
        const state = this.eudiwQueryState();
        return 'query' in state ? state.query ?? null : null;
    } );

    /** The current resource retrieval error, or `null` while no such error is shown. */
    protected readonly eudiwApplyErrorReason = computed<'unavailable' | 'queryNotFound' | 'technical' | null>( () => {
        const state = this.eudiwApplyState();
        return state.status === 'error' ? state.reason : null;
    } );

    /** Invalidates responses belonging to an earlier panel or dialog state. */
    private eudiwRequestGeneration = 0;

    /**
     * Hint shown above the source panels, mirroring the JSF
     * {@code isShowEditSelectionHelptext()}. Empty when the text is not maintained.
     *
     * Resolved once while this component is being created, which is safe: the application
     * only starts to render after {@code HisBootstrapService} has loaded the texts of the
     * user's language, and a change of that language reloads the whole page.
     */
    protected readonly editSelectionHelptext = this.hisUiService.translateOrEmpty( 'common.components.selectFile.editSelection.helptext' );

    /** The {@link localMedia} keyed by their media reference, used to resolve a selection to its display data. */
    private readonly localMediaByReference = computed<Map<string, LocalMedia>>( () =>
        new Map( this.localMedia().map( ( item ) => [item.mediaReference, item] ) )
    );

    /**
     * The media references of the local-media files that are already selected. Those may not
     * be picked a second time, because {@link hisSelectFileKey} identifies a local-media
     * selection by exactly that reference.
     */
    protected readonly selectedLocalMediaReferences = computed<string[]>( () =>
        ( this.value() ?? [] )
            .map( ( file ) => file.localMedia?.mediaReference )
            .filter( ( reference ): reference is string => reference != null )
    );

    /** Selectable media, excluding persistent uploads seen so far and the current local-media selection. */
    protected readonly filteredLocalMedia = computed<LocalMedia[]>( () => {
        // Include the current value immediately, even before the retention effect has run.
        const excludedGuids = withPersistentFileObjGuids(
            this.excludedPersistentFileObjGuids(),
            selectionOf( this.value() )
        );
        const selectedReferences = new Set( this.selectedLocalMediaReferences() );

        return this.localMedia().filter( ( media ) => {
            const guid = documentMetadataObjGuidOf( media.mediaReference );
            return !selectedReferences.has( media.mediaReference )
                && ( guid == null || !excludedGuids.has( guid ) );
        } );
    } );

    /**
     * `true` while the embedded upload component is actively uploading a file
     * (and the transfer is not fully paused). Used to disable the overlay's
     * apply/cancel actions during an active upload, mirroring the JSF behaviour.
     */
    protected readonly isUploadActive = computed<boolean>( () => this.fileUpload()?.uploadActive() ?? false );

    constructor() {
        super();

        // Angular forms caches the validation result; the configuration and the required
        // input decide whether — and what — has to be validated at all, so a change of
        // either invalidates that cache. A changed selection does not need to be tracked
        // here: the form revalidates its control whenever its value changes anyway.
        effect( () => {
            this.configuration();
            this.isRequired();
            this.onValidatorChange?.();
        } );

        // Whenever the selection or the derived common category changes, make sure the
        // category is also carried by the selected files themselves.
        effect( () => {
            this.value();
            this.categories.selectedId();

            // Deferred on purpose: the effect runs during change detection and writing the
            // model (and thus flipping the form's validity) from there would raise NG0100
            // in components that bind to the form state, e.g. a submit button's [disabled].
            queueMicrotask( () => this.categories.materialize() );
        } );

        // A consumer may write the selection through the two-way binding [(value)], which
        // bypasses every code path that reports the change itself. Such a write is reported
        // here, so that (selectionChange) really sees every change of the selection.
        effect( () => {
            const files = this.value();
            // Direct [(value)] writes also contribute to the lifetime exclusion set.
            untracked( () => this.rememberPersistentFiles( selectionOf( files ) ) );
            if ( files === this.emittedSelection ) {
                return;
            }

            this.emittedSelection = files;

            // Deferred on purpose: the effect runs during change detection and a consumer
            // usually stores the reported event for its own template, which would raise
            // NG0100 when done while that template is already being checked.
            queueMicrotask( () => {
                if ( this.value() === files ) {
                    this.emitSelectionChange( 'write' );
                }
            } );
        } );

        // The name and the download of an already selected local-media file are taken from the
        // local media, so a selection carrying one needs them without waiting for the overlay.
        effect( () => {
            if ( this.selectedLocalMediaReferences().length > 0 ) {
                this.loadLocalMedia().pipe( take( 1 ), takeUntilDestroyed( this.destroyRef ) ).subscribe();
            }
        } );

        // The local media are what the local-media panel shows, so they are loaded the moment it
        // is opened - a component whose panel is never opened does not cost a request.
        effect( () => {
            if ( this.localMediaPanelExpanded() ) {
                this.loadLocalMedia().pipe( take( 1 ), takeUntilDestroyed( this.destroyRef ) ).subscribe();
            }
        } );

        let eudiwWasExpanded = false;
        effect( () => {
            const expanded = this.eudiwPanelExpanded();

            untracked( () => {
                if ( expanded && !eudiwWasExpanded && this.eudiwQueryState().status === 'idle' ) {
                    this.createEudiwQuery();
                } else if ( !expanded && eudiwWasExpanded ) {
                    this.invalidateEudiwRequests();
                }

                eudiwWasExpanded = expanded;
            } );
        } );
    }

    /**
     * Loads the local media of the current {@link configKey}, at most once per key. A failing
     * request degrades to an empty list, which the local-media panel presents as "no files".
     *
     * Deliberately not loaded while the component is being created: the list is only needed
     * once the local-media panel shows its content — or once a local-media file is part of the
     * selection and has to be presented with its name and its download.
     */
    private loadLocalMedia(): Observable<LocalMedia[]> {
        const configKey = this.configKey();

        if ( !this.localMediaRequest || this.localMediaRequestKey !== configKey ) {
            this.localMediaRequestKey = configKey;
            this.localMediaLoaded.set( false );
            this.localMedia.set( [] );
            this.localMediaRequest = this.selectFileService.listLocalMedia( {
                configKey,
                // TODO: add javascriptContext
                // TODO: add contextObject
            } ).pipe(
                catchError( ( error ) => {
                    console.error( 'his-select-file: could not load the local media for', configKey, error );
                    return of( [] as LocalMedia[] );
                } ),
                tap( ( media ) => {
                    this.localMedia.set( media );
                    this.localMediaLoaded.set( true );
                } ),
                shareReplay( { bufferSize: 1, refCount: false } )
            );
        }

        return this.localMediaRequest;
    }

    /**
     * Adopts the selection after the category coordinator has assigned the common category
     * to every file, and reports that change like any other one.
     *
     * Never call this during change detection: writing the model (and thus flipping the
     * form's validity) would raise NG0100 in components that bind to the form state, e.g. a
     * submit button's {@code [disabled]}.
     */
    private applyCategorizedFiles( files: HisSelectFile[] ): void {
        this.setSelectionValue( files );
        this.onModelChange( files );
        this.onValidatorChange?.();
        this.emitSelectionChange( 'category' );
    }

    /** Applies the category picked by the user to all selected files. */
    protected onCategoryChange( category: GenericValue | null ): void {
        this.categories.choose( category );
        this.onModelTouched();
    }

    // ControlValueAccessor
    public override writeValue( value: HisSelectFile[] | undefined ): void {
        this.setSelectionValue( selectionOf( value ) );
        this.emitSelectionChange( 'write' );
    }

    /** Retains both selections synchronously, before removal or replacement can discard a UUID. */
    private setSelectionValue( files: HisSelectFile[] | undefined ): void {
        this.rememberPersistentFiles( selectionOf( this.value() ) );
        this.rememberPersistentFiles( files );
        this.value.set( files );
    }

    private rememberPersistentFiles( files: HisSelectFile[] | undefined ): void {
        this.excludedPersistentFileObjGuids.update( guids => withPersistentFileObjGuids( guids, files ) );
    }

    public override setDisabledState( isDisabled: boolean ): void {
        this.disabledByForm.set( isDisabled );
    }

    // Validator
    /**
     * Validates the selection carried by {@code control}. The files are taken from the
     * control rather than from {@link value}, because the form asks for the state it holds
     * itself — which is not necessarily the state the component has already adopted.
     */
    validate( control: AbstractControl ): ValidationErrors | null {
        const files: HisSelectFile[] = selectionOf( control?.value ) ?? [];

        const errors: ValidationErrors = {
            ...this.validateRequired( files ),
            ...this.categories.validate( files ),
        };

        return Object.keys( errors ).length > 0 ? errors : null;
    }

    private validateRequired( files: HisSelectFile[] ): ValidationErrors | null {
        if ( !this.isRequired() ) {
            return null;
        }

        if ( files.length > 0 ) {
            return null;
        }

        return {
            filesMissing: {
                message: this.hisUiService.translate( 'validation.required' )
            }
        };
    }

    /**
     * Mirrors the JSF {@code required="true"} of the category dropdown: as soon as the
     * category has to be presented, every selected file needs to carry one.
     */
    registerOnValidatorChange( fn: () => void ): void {
        this.onValidatorChange = fn;
    }

    protected fileKey( file: HisSelectFile ): string {
        return hisSelectFileKey( file );
    }

    /**
     * Emits {@link selectionChange} with a snapshot of the current selection.
     * Called from every code path that writes {@link value}.
     */
    private emitSelectionChange( reason: HisSelectFileChangeReason ): void {
        this.emittedSelection = this.value();
        const files = this.emittedSelection ?? [];

        this.selectionChange.emit( {
            reason,
            files,
            count: files.length,
            empty: files.length === 0,
        } );
    }

    protected editSelection(): void {
        this.uploadDraft.set( [] );
        this.resetEudiwSelection();
        this.activeSelectionPanel.set( this.defaultSelectionPanel() );
        this.showChangeDialog.set( true );
    }

    /**
     * The accordion panel expanded when the selection overlay opens, mirroring the JSF
     * {@code getEditOverlayDefaultTabIndex()}: the only available source — or none at all
     * when the user really has a choice between several sources.
     */
    private defaultSelectionPanel(): string | null {
        const panels = this.availableSelectionPanels();
        return panels.length === 1 ? panels[0] : null;
    }

    /** Creates or replaces the query displayed in the EUDI-Wallet panel. */
    protected createEudiwQuery(): void {
        const generation = ++this.eudiwRequestGeneration;
        const previousQuery = this.eudiwQuery();
        this.eudiwQueryState.set( { status: 'loading', query: previousQuery ?? undefined } );
        this.eudiwApplyState.set( { status: 'idle' } );

        this.selectFileService.createEudiwQuery( { configKey: this.configKey() } ).pipe(
            take( 1 ),
            takeUntilDestroyed( this.destroyRef )
        ).subscribe( {
            next: ( query ) => {
                if ( !this.isCurrentEudiwRequest( generation ) || !this.validEudiwQuery( query ) ) {
                    if ( this.isCurrentEudiwRequest( generation ) ) {
                        this.eudiwQueryState.set( { status: 'error', query: previousQuery ?? undefined } );
                    }
                    return;
                }

                this.eudiwQueryState.set( { status: 'ready', query } );
            },
            error: () => {
                if ( this.isCurrentEudiwRequest( generation ) ) {
                    this.eudiwQueryState.set( { status: 'error', query: previousQuery ?? undefined } );
                }
            },
        } );
    }

    /** Retrieves the resource supplied for the current query and applies it to the selection. */
    protected applyEudiw(): void {
        const queryState = this.eudiwQueryState();
        if ( queryState.status !== 'ready' || this.eudiwApplyState().status === 'loading' ) {
            return;
        }

        const generation = ++this.eudiwRequestGeneration;
        const queryId = queryState.query.queryId;
        this.eudiwApplyState.set( { status: 'loading' } );

        this.selectFileService.getEudiwMedia( queryId ).pipe(
            take( 1 ),
            takeUntilDestroyed( this.destroyRef )
        ).subscribe( {
            next: ( media ) => {
                if ( !this.isCurrentEudiwRequest( generation ) ) {
                    return;
                }

                if ( !media?.mediaReference ) {
                    this.eudiwApplyState.set( { status: 'error', reason: 'technical' } );
                    return;
                }

                this.applySelection( [this.toEudiwSelectFile( media )] );
            },
            error: ( error: HttpErrorResponse ) => {
                if ( !this.isCurrentEudiwRequest( generation ) ) {
                    return;
                }

                const reason = this.eudiwErrorReason( error );
                if ( reason === 'queryNotFound' ) {
                    this.eudiwQueryState.set( { status: 'error' } );
                }
                this.eudiwApplyState.set( { status: 'error', reason } );
            },
        } );
    }

    private validEudiwQuery( query: EudiwMediaQuery | null ): query is EudiwMediaQuery {
        return Boolean( query?.queryId && query.redirectUrl && query.qrCodeImageData );
    }

    private eudiwErrorReason( error: HttpErrorResponse ): 'unavailable' | 'queryNotFound' | 'technical' {
        const code = error?.error?.code;
        if ( error?.status === 404 && code === 'EUDIW_QUERY_NOT_FOUND' ) {
            return 'queryNotFound';
        }
        if ( error?.status === 409 && code === 'EUDIW_RESOURCE_UNAVAILABLE' ) {
            return 'unavailable';
        }
        return 'technical';
    }

    private isCurrentEudiwRequest( generation: number ): boolean {
        return generation === this.eudiwRequestGeneration
            && this.showChangeDialog()
            && this.eudiwPanelExpanded();
    }

    private invalidateEudiwRequests(): void {
        this.eudiwRequestGeneration++;
        if ( this.eudiwQueryState().status === 'loading' ) {
            this.eudiwQueryState.set( { status: 'idle' } );
        }
        if ( this.eudiwApplyState().status === 'loading' ) {
            this.eudiwApplyState.set( { status: 'idle' } );
        }
    }

    private resetEudiwSelection(): void {
        this.eudiwRequestGeneration++;
        this.eudiwQueryState.set( { status: 'idle' } );
        this.eudiwApplyState.set( { status: 'idle' } );
    }

    /** The {@link LocalMedia} a local-media selection refers to, or `undefined` for an upload. */
    protected localMediaOf( file: HisSelectFile ): LocalMedia | undefined {
        const reference = file.localMedia?.mediaReference;
        return reference == null ? undefined : this.localMediaByReference().get( reference );
    }

    protected disableDownloadOf( file: HisSelectFile ): boolean {
        return file.indicators?.includes( 'disableDownload' ) ?? false;
    }

    /**
     * The name the given file is presented with in its {@code <dt>}. Resolved here, because the
     * {@code <dt>} is not part of the {@link HisSelectFileItemComponent} rendering the
     * neighbouring {@code <dd>}: a {@code <dl>} takes no element between itself and its
     * {@code <dt>}/{@code <dd>}.
     */
    protected filenameOf( file: HisSelectFile ): string | null {
        return hisSelectFileName( file, this.localMediaOf( file ) );
    }

    /**
     * Applies the current upload draft: wraps every uploaded {@link HisUploadFile}
     * in a new {@link HisSelectFile} (source type {@link UploadSourceType.UPLOAD}).
     * When the configuration allows {@code multiple} files, the wrapped selections
     * are appended to the existing {@link value}; otherwise they replace the
     * current selection. Afterwards the draft and the embedded upload component
     * are reset and the overlay is closed.
     */
    protected applyUpload(): void {
        const uploaded = this.uploadDraft() ?? [];

        if ( uploaded.length === 0 ) {
            // Nothing selected: surface the embedded upload field's own validation
            // output (e.g. the standard "required" message in the surrounding
            // <his-plam>) and keep the overlay open while the field stays invalid.
            const control = this.uploadModel()?.control;
            if ( control ) {
                triggerControlValidation( control );
                if ( control.invalid ) {
                    return;
                }
            }

            this.closeSelection();
            return;
        }

        const added = uploaded.map( ( upload ) => this.toSelectFile( upload ) );
        this.applySelection( added );
    }

    /**
     * Applies the local-media file the user picked in the local-media panel, mirroring the
     * JSF {@code selectLocalMediaResource()}: the selection takes effect immediately, so the
     * panel has no separate "apply" action.
     */
    protected applyLocalMedia( media: LocalMedia ): void {
        this.applySelection( [this.toLocalMediaSelectFile( media )] );
    }

    /**
     * Adds the given selections to {@link value} and closes the overlay. When the
     * configuration allows {@code multiple} files they are appended to the existing
     * selection; otherwise they replace it. Files beyond the configured limit are
     * dropped by {@link withinFileLimit}.
     */
    private applySelection( added: HisSelectFile[] ): void {
        const next = this.withinFileLimit( this.configuration()?.multiple
            ? [...( this.value() ?? [] ), ...added]
            : added );

        const applied = added.find( ( file ) => next.includes( file ) );

        this.setSelectionValue( next );
        this.onModelChange( next );
        this.onModelTouched();
        this.emitSelectionChange( 'add' );

        this.focusAppliedFile( applied );
        this.closeSelection();
    }

    /**
     * Moves the focus to the file that was just applied, mirroring what the user did: the button
     * they used is gone with the overlay, so without this the focus would fall back to the button
     * that opened it — or, without the global focus restore, to the beginning of the page.
     *
     * The file is not rendered yet, so the overlay is handed a look-up carried out once the focus
     * is actually restored.
     */
    private focusAppliedFile( applied: HisSelectFile | undefined ): void {
        const dialog = this.selectionDialog();

        if ( applied == null || dialog == null ) {
            return;
        }

        const key = hisSelectFileKey( applied );
        this.restoreFocus.restoreTo( dialog,
            () => this.fileItems().find( ( item ) => hisSelectFileKey( item.file() ) === key )?.focusTarget() ?? null );
    }

    /**
     * The given selection, cut down to the number of files the configuration allows: the
     * {@code fileLimit} for a multiple selection, a single file otherwise. A selection
     * without a configuration or without a limit is taken as it is.
     *
     * The user cannot run into this — {@link canEditSelection} hides the action once the
     * limit is reached, and the embedded upload contributes one file at a time. It is a
     * guard against the ways around that: a programmatic call, or a {@link value} written
     * from the outside while the overlay is open. Because that means someone lost track of
     * the limit, the discarded files are reported.
     */
    private withinFileLimit( files: HisSelectFile[] ): HisSelectFile[] {
        const config = this.configuration();
        const limit = config?.multiple ? config.fileLimit : ( config ? 1 : 0 );

        if ( limit <= 0 || files.length <= limit ) {
            return files;
        }

        console.error( 'his-select-file: the selection for', this.configKey(),
            'exceeds its file limit of', limit, '- discarding', files.slice( limit ) );

        return files.slice( 0, limit );
    }

    /**
     * Wraps a single uploaded {@link HisUploadFile} in a new {@link HisSelectFile}
     * with source type {@link UploadSourceType.UPLOAD}.
     */
    private toSelectFile( upload: HisUploadFile ): HisSelectFile {
        return {
            sourceType: UploadSourceType.UPLOAD,
            upload,
            documentMetadataCategoryId: this.categories.forNewFile(),
        };
    }

    /**
     * Wraps a single {@link LocalMedia} item in a new {@link HisSelectFile} with source type
     * {@link UploadSourceType.LOCAL_MEDIA}. Only the media reference is carried over — the
     * server resolves the actual resource from it.
     */
    private toLocalMediaSelectFile( media: LocalMedia ): HisSelectFile {
        return {
            sourceType: UploadSourceType.LOCAL_MEDIA,
            localMedia: { mediaReference: media.mediaReference },
            documentMetadataCategoryId: this.categories.forNewFile(),
        };
    }

    /** Wraps a resource supplied by an EUDI wallet in the common selection model. */
    private toEudiwSelectFile( media: EudiwMedia ): HisSelectFile {
        return {
            sourceType: UploadSourceType.EUDIW,
            eudiwMedia: media,
            documentMetadataCategoryId: this.categories.forNewFile(),
        };
    }

    /**
     * Keeps the caret navigation keys inside a text field of an accordion panel.
     *
     * PrimeNG's {@code Accordion} registers its header navigation on the host element of the
     * whole accordion and handles the arrow, home and end keys without checking where the
     * event came from. Without this guard it calls {@code preventDefault()} on keys pressed
     * inside a panel as well, which makes text fields in a panel impossible to navigate.
     *
     * Everything else keeps reaching the accordion, so its keyboard navigation stays intact
     * wherever the caret is not the one meant by the key.
     */
    protected keepCaretKeysInPanel( event: KeyboardEvent ): void {
        const target = event.target;
        const editsText = target instanceof HTMLInputElement
            || target instanceof HTMLTextAreaElement
            || ( target instanceof HTMLElement && target.isContentEditable );

        if ( editsText && ACCORDION_NAVIGATION_KEYS.includes( event.code ) ) {
            event.stopPropagation();
        }
    }

    /**
     * Cancels the current selection: closes the overlay without touching {@link value}.
     */
    protected cancelSelection(): void {
        this.closeSelection();
    }

    /** Closes the overlay; {@link onSelectionClosed} discards its state once it is gone. */
    private closeSelection(): void {
        this.showChangeDialog.set( false );
    }

    /**
     * Discards the upload draft once the overlay has closed — through one of its buttons, the
     * close icon, [ESC] or a click on the backdrop alike. The embedded upload and local-media
     * panels need no reset: they are created anew with the overlay.
     */
    protected onSelectionClosed(): void {
        this.uploadDraft.set( [] );
        this.activeSelectionPanel.set( null );
        this.resetEudiwSelection();
    }

    protected removeFile( file: HisSelectFile ): void {
        const key = hisSelectFileKey( file );
        const remaining = ( this.value() ?? [] ).filter( ( f ) => hisSelectFileKey( f ) !== key );

        this.setSelectionValue( remaining );
        this.onModelChange( remaining );
        this.onModelTouched();
        this.emitSelectionChange( 'remove' );
    }

    /**
     * Applies a new comment to the given file, mirroring the JSF {@code saveComment()}.
     * The affected element is replaced by a copy carrying the new comment so that the
     * change is picked up by Angular's change detection; a blank comment is stored as
     * {@code null} (and not as {@code undefined}) so that clearing a comment is still
     * serialized when the value is sent to the server.
     *
     * The comment is mirrored into the wrapped {@link HisUploadFile}, because the server
     * persists the comment of an already uploaded file from that nested property.
     */
    protected updateComment( file: HisSelectFile, comment: string ): void {
        const key = hisSelectFileKey( file );
        const newComment = trimToNull( comment );
        const next = ( this.value() ?? [] ).map( ( f ) =>
            hisSelectFileKey( f ) === key
                ? {
                    ...f,
                    comment: newComment,
                    upload: f.upload ? { ...f.upload, comment: newComment } : f.upload
                }
                : f
        );

        this.setSelectionValue( next );
        this.onModelChange( next );
        this.onModelTouched();
        this.emitSelectionChange( 'comment' );
    }
}
